import { StoreSubject } from "../../../../src/lib/store";
import {
  FX_HEIGHT_DEFAULT,
  INSTRUMENT_WIDTH_DEFAULT,
  PATTERNS_WIDTH_DEFAULT,
} from "../lib/dockSizes";
import { DEFAULT_MATERIAL, type MaterialMode } from "../lib/material";
import { createDefaultMuteSolo } from "../lib/muteSolo";
import type { InstrumentSlot } from "../../../../src/lib/audio";
import type { PatternLoopRange } from "../lib/patternLoop";

export type LabUiState = {
  statusMessage: string;
  statusOpen: boolean;
  dslOpen: boolean;
  /** Editable Pattern DSL text (Apply / Grid→DSL read/write this). */
  dslDraft: string;
  /** Parse / apply warnings shown under the DSL textarea. */
  dslWarnings: string;
  fxOpen: boolean;
  /** When true, live playback skips the master FX stack (project FX unchanged). */
  fxBypass: boolean;
  /** Which stack the FX dock edits: master bus or the active instrument. */
  fxScope: "master" | "track";
  /** Bottom FX panel height in px (drag-resized). */
  fxHeight: number;
  patternsOpen: boolean;
  /** Arrange / Samples sidebar width in px (drag-resized). */
  patternsWidth: number;
  /** Sample manager panel in the right sidebar. */
  samplesOpen: boolean;
  /** Active instrument slot when the left dock is open; null when closed. */
  instrumentSlot: InstrumentSlot | null;
  /** Left instrument sidebar width in px (drag-resized). */
  instrumentWidth: number;
  /** Tracker mute flags (mirrors project instruments.length). */
  trackMuted: boolean[];
  /** Tracker solo flags (mirrors project instruments.length). */
  trackSolo: boolean[];
  material: MaterialMode;
  activePatternId: string;
  /** Loop brace — contiguous arrange range over clip.patterns. */
  patternLoop: PatternLoopRange;
  activeSceneId: string;
  helpOpen: boolean;
  goToOpen: boolean;
  /** Floating Generate Notes tool (tracker selection fill). */
  generateNotesOpen: boolean;
  /** Floating Mixer panel (tracks + master). */
  mixerOpen: boolean;
  /** Floating Performance (session) grid. */
  performanceOpen: boolean;
  /** Floating Settings (MIDI / control routing). */
  settingsOpen: boolean;
  /** Floating synth author modal. */
  authorOpen: boolean;
  /** Row label editor anchored to a tracker step cell. */
  rowLabelEdit: RowLabelEditState | null;
  /** Track name + color editor anchored to a track header. */
  trackLabelEdit: TrackLabelEditState | null;
  /** Offline bounce / stems progress overlay. */
  bounce: BounceUiState;
};

export type BounceUiState = {
  active: boolean;
  title: string;
  /** 0..1 */
  progress: number;
};

export type RowLabelEditState = {
  step: number;
  label: string;
  color: number;
  anchor: { left: number; top: number; bottom: number; right: number };
};

export type TrackLabelEditState = {
  track: number;
  /** `null` = no project tint. */
  color: number | null;
  anchor: { left: number; top: number; bottom: number; right: number };
};

const initial: LabUiState = {
  statusMessage: "Engine not started.",
  statusOpen: false,
  dslOpen: false,
  dslDraft: "",
  dslWarnings: "",
  fxOpen: false,
  fxBypass: false,
  fxScope: "master",
  fxHeight: FX_HEIGHT_DEFAULT,
  patternsOpen: false,
  patternsWidth: PATTERNS_WIDTH_DEFAULT,
  samplesOpen: false,
  instrumentSlot: null,
  instrumentWidth: INSTRUMENT_WIDTH_DEFAULT,
  trackMuted: createDefaultMuteSolo(),
  trackSolo: createDefaultMuteSolo(),
  material: DEFAULT_MATERIAL,
  activePatternId: "P0",
  patternLoop: { start: 0, length: 1 },
  activeSceneId: "S0",
  helpOpen: false,
  goToOpen: false,
  generateNotesOpen: false,
  mixerOpen: false,
  performanceOpen: false,
  settingsOpen: false,
  authorOpen: false,
  rowLabelEdit: null,
  trackLabelEdit: null,
  bounce: { active: false, title: "Bouncing to WAV…", progress: 0 },
};

export const uiStore = new StoreSubject<LabUiState>(initial);

export function patchUi(partial: Partial<LabUiState>): void {
  uiStore.next({ ...uiStore.getValue(), ...partial });
}

export function setStatusMessage(message: string): void {
  patchUi({ statusMessage: message });
}
